import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import {
  ArrowRight,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  Briefcase,
  ChevronRight,
  Clock,
  Code2,
  Database,
  GraduationCap,
  PlayCircle,
  Route,
  Send,
  Sheet,
} from 'lucide-react';
import { careersApi, coursesApi } from '@/api/endpoints';
import type { CareerSummaryResponse, CourseSummaryResponse } from '@/api/types';
import { useSeo } from '@/hooks/useSeo';
import { pageSeoTitle } from '@/lib/seoText';
import { formatTotalDuration } from '@/components/courses/duration';
import { monthlyInstallment, PURCHASE_CONTACT_URL } from '@/components/courses/purchaseContact';
import { useCourseAccess } from '@/components/courses/useCourseAccess';
import { useLocalized } from '@/hooks/useLocalized';
import { formatSom } from '@/lib/format';
import { COURSES_INDEX_PATH, careerPath, coursePath, coursesIndexStructuredData } from '@/lib/structuredData';
import { useInitialData } from '@/ssr/initialData';

const TABS = ['courses', 'careers'] as const;
type Tab = (typeof TABS)[number];

/** Kurslar katalogi: /courses — "Kurslar" / "Kasblar" tablari, standart holatda kurslar */
export function Courses() {
  const { t } = useTranslation();
  const initial = useInitialData();
  const [courses, setCourses] = useState<CourseSummaryResponse[] | null>(() => initial?.courses ?? null);
  const [careers, setCareers] = useState<CareerSummaryResponse[]>(() => initial?.careers ?? []);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState<Tab>('courses');

  useEffect(() => {
    let cancelled = false;
    coursesApi
      .list()
      .then((res) => {
        if (!cancelled) setCourses(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    careersApi
      .list()
      .then((res) => {
        if (!cancelled) setCareers(res.data);
      })
      .catch(() => {
        /* kasblar ixtiyoriy bo'lim */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useSeo({
    title: pageSeoTitle("Video kurslar va kasblar — noldan o'rganing"),
    description:
      "SQL, Excel, Python va pandas bo'yicha o'zbek tilidagi video kurslar va kasb yo'llari: qisqa darslar, konspektlar, amaliy mashqlar. Birinchi modul bepul.",
    path: COURSES_INDEX_PATH,
    // Kurs hali yo'q — bo'sh sahifa indekslanmasin (sitemap ham uni bermaydi)
    noindex: courses !== null && courses.length === 0 && careers.length === 0,
    jsonLd: coursesIndexStructuredData(),
  });

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to="/" className="transition-colors hover:text-content">
              {t('nav.home')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="text-content">
            {t('nav.courses')}
          </li>
        </ol>
      </nav>

      <div className="mb-8">
        <h1 className="text-2xl font-bold sm:text-3xl">{t('courses.title')}</h1>
        <p className="mt-2 max-w-3xl text-content-secondary">{t('courses.subtitle')}</p>
      </div>

      <InstallmentBanner />

      <div role="tablist" aria-label={t('courses.title')} className="mb-6 inline-flex rounded-lg border border-line bg-surface p-1">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`courses-tab-${key}`}
            aria-selected={tab === key}
            aria-controls={`courses-panel-${key}`}
            onClick={() => setTab(key)}
            className={cn(
              'inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
              tab === key ? 'bg-brand-600 text-white shadow-sm' : 'text-content-secondary hover:text-content',
            )}
          >
            {key === 'courses' ? <BookOpen className="h-4 w-4" /> : <Briefcase className="h-4 w-4" />}
            {t(key === 'courses' ? 'courses.coursesTitle' : 'courses.careersTitle')}
          </button>
        ))}
      </div>

      {/* Nofaol panel HTML da qoladi (hidden) — prerender va crawler ikkala ro'yxatdagi havolalarni ham ko'radi */}
      <section id="courses-panel-courses" role="tabpanel" aria-labelledby="courses-tab-courses" hidden={tab !== 'courses'}>
        {courses === null ? (
          error ? (
            <p className="py-12 text-center text-content-secondary">{t('courses.loadError')}</p>
          ) : (
            <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <li key={i} className="card h-48 animate-pulse" />
              ))}
            </ul>
          )
        ) : courses.length === 0 ? (
          <EmptyState icon={<BookOpen className="h-10 w-10 text-content-muted" />} text={t('courses.empty')} />
        ) : (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => (
              <li key={c.slug}>
                <CourseCard course={c} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section id="courses-panel-careers" role="tabpanel" aria-labelledby="courses-tab-careers" hidden={tab !== 'careers'}>
        {careers.length === 0 ? (
          <EmptyState icon={<Briefcase className="h-10 w-10 text-content-muted" />} text={t('courses.careersEmpty')} />
        ) : (
          <>
            <p className="mb-4 text-sm text-content-secondary">{t('courses.careersSubtitle')}</p>
            <ul className="grid gap-5 lg:grid-cols-2">
              {careers.map((c) => (
                <li key={c.slug}>
                  <CareerCard career={c} />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

function CareerCard({ career }: { career: CareerSummaryResponse }) {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const { hasCareer } = useCourseAccess();
  return (
    <Link
      to={careerPath(career.slug)}
      className="card card-hover group flex h-full flex-col border-brand-500/30 bg-gradient-to-br from-brand-500/10 to-transparent p-6"
    >
      <div className="mb-3 flex items-center gap-2 text-brand-600">
        <Briefcase className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-wide">{t('courses.career.label')}</span>
      </div>
      <h3 className="text-xl font-bold transition-colors group-hover:text-brand-600">{career.title}</h3>
      <p className="mt-2 flex-1 text-sm text-content-secondary">{career.shortDescription}</p>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-content-secondary">
        <span className="inline-flex items-center gap-1.5">
          <Route className="h-4 w-4" />
          {t('courses.career.steps', { count: career.stepCount })}
          {career.availableCount < career.stepCount && (
            <span className="text-content-muted">· {t('courses.career.available', { count: career.availableCount })}</span>
          )}
        </span>
        {career.salaryText && (
          <span>
            {t('courses.career.salary')}: <span className="font-semibold text-content">{career.salaryText}</span>
          </span>
        )}
        {hasCareer(career.slug) ? (
          <span className="inline-flex items-center gap-1 font-semibold text-[var(--easy)]">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            {t('courses.purchase.owned')}
          </span>
        ) : (
          career.priceUzs != null && (
            <span className="ml-auto flex flex-col items-end">
              <span className="text-base font-bold text-content">{formatSom(career.priceUzs, lang)}</span>
              <InstallmentLine priceUzs={career.priceUzs} />
            </span>
          )
        )}
      </div>
    </Link>
  );
}

/** Kurs mavzusiga qarab muqova belgisi va rangi (sarlavhadan aniqlanadi) */
const COURSE_THEMES = [
  { match: /python|pandas/i, icon: Code2, gradient: 'from-sky-500 to-indigo-600' },
  { match: /excel/i, icon: Sheet, gradient: 'from-emerald-500 to-teal-600' },
  { match: /sql|postgres/i, icon: Database, gradient: 'from-brand-500 to-violet-600' },
] as const;

function courseTheme(title: string) {
  return COURSE_THEMES.find((th) => th.match.test(title)) ?? { icon: GraduationCap, gradient: 'from-slate-500 to-slate-700' };
}

function CourseCard({ course }: { course: CourseSummaryResponse }) {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const { hasCourse } = useCourseAccess();
  const owned = hasCourse(course.slug);
  const { icon: Icon, gradient } = courseTheme(course.title);
  return (
    <Link
      to={coursePath(course.slug)}
      className="card group flex h-full flex-col overflow-hidden transition-all hover:-translate-y-0.5 hover:border-brand-500/40 hover:shadow-glow"
    >
      <div className={cn('relative flex h-28 items-end justify-between bg-gradient-to-br p-4 text-white', gradient)}>
        <Icon className="absolute -right-3 -top-3 h-28 w-28 opacity-[0.15]" aria-hidden />
        <span className="rounded-md bg-white/20 px-2 py-0.5 text-xs font-semibold backdrop-blur-sm">
          {t(`courses.level.${course.level}`)}
        </span>
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
          <Icon className="h-6 w-6" aria-hidden />
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <h3 className="text-lg font-semibold leading-snug transition-colors group-hover:text-brand-600">{course.title}</h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-content-secondary">{course.shortDescription}</p>

        <ul className="mb-5 mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-content-secondary">
          <li className="inline-flex items-center gap-1.5">
            <PlayCircle className="h-4 w-4 text-content-muted" aria-hidden />
            {t('courses.lessons', { count: course.lessonCount })}
          </li>
          {course.totalDurationSec > 0 && (
            <li className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-content-muted" aria-hidden />
              {formatTotalDuration(course.totalDurationSec, t)}
            </li>
          )}
          {course.exerciseCount > 0 && (
            <li className="inline-flex items-center gap-1.5">
              <Code2 className="h-4 w-4 text-content-muted" aria-hidden />
              {t('courses.exercises', { count: course.exerciseCount })}
            </li>
          )}
          {(owned || course.priceUzs != null) && course.freeLessonCount > 0 && (
            <li className="font-medium text-[var(--easy)]">
              {t('courses.freeLessons', { count: course.freeLessonCount })}
            </li>
          )}
        </ul>

        <div className="mt-auto flex items-center justify-between gap-3 border-t border-line pt-4">
          {owned ? (
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--easy)]">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {t('courses.purchase.owned')}
            </span>
          ) : course.priceUzs != null ? (
            <span className="flex flex-col">
              <span className="text-base font-bold">{formatSom(course.priceUzs, lang)}</span>
              <InstallmentLine priceUzs={course.priceUzs} />
            </span>
          ) : course.freeLessonCount > 0 ? (
            <span className="rounded-full bg-[var(--easy)]/15 px-2.5 py-0.5 text-xs font-semibold text-[var(--easy)]">
              {t('courses.freeLessons', { count: course.freeLessonCount })}
            </span>
          ) : (
            <span />
          )}
          <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium text-brand-600">
            {t('courses.viewCourse')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
          </span>
        </div>
      </div>
    </Link>
  );
}

/** Karta narxi ostida: "oyiga 83 167 so'm" + "6 oy · Alif Nasiya" */
function InstallmentLine({ priceUzs }: { priceUzs: number }) {
  const { t } = useTranslation();
  const { lang } = useLocalized();
  const monthly = monthlyInstallment(priceUzs);
  if (monthly == null) return null;
  return (
    <>
      <span className="whitespace-nowrap text-xs font-semibold text-brand-600 dark:text-brand-400">
        {t('courses.purchase.installmentMonthly', { amount: formatSom(monthly, lang) })}
      </span>
      <span className="whitespace-nowrap text-[11px] text-content-muted">{t('courses.purchase.installmentProvider')}</span>
    </>
  );
}

/** Kurs va kasblarni Alif Nasiya orqali bo'lib to'lash — rasmiylashtirish admin bilan Telegram'da */
function InstallmentBanner() {
  const { t } = useTranslation();
  const href = `${PURCHASE_CONTACT_URL}?text=${encodeURIComponent(t('courses.installment.message'))}`;
  return (
    <aside
      aria-label={t('courses.installment.title')}
      className="mb-6 flex flex-col gap-4 rounded-xl border border-brand-500/25 bg-brand-500/[0.06] p-5 sm:flex-row sm:items-center"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white" aria-hidden>
        <CalendarClock className="h-5 w-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-content">{t('courses.installment.title')}</p>
        <p className="mt-1 text-sm text-content-secondary">{t('courses.installment.text')}</p>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="btn btn-secondary inline-flex shrink-0 gap-2 px-4 py-2.5 text-sm font-semibold"
      >
        <Send className="h-4 w-4" aria-hidden />
        {t('courses.installment.cta')}
      </a>
    </aside>
  );
}

function EmptyState({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="card flex flex-col items-center gap-3 p-12 text-center text-content-secondary">
      {icon}
      <p>{text}</p>
    </div>
  );
}
